// EWM-743 — a product's carton quantity (OneOffice TU5).
//
// It is NOT a top-level CT variant attribute: it is a key NESTED inside the
// existing `freightAttributes` JSON attribute — the same payload
// `derivePackSize` (EWM-315) reads for the "Sold as" row.
//
// Confirmed by Leo Ryan on the ticket 2026-09-09 ("outer_tu5item_quantity",
// inside freightAttributes, "the same you pull uom from"), and verified
// against electus-dev on 2026-09-11: 5 of 8,929 variants carry a value, in
// BOTH shapes — a JSON number (QM1323 = 30, SB2486 / SB2487 = 4) and a
// decimal-formatted string (QM1500 = "60.0", WB1500 = "800.0").
//
// A sibling key in that same payload, `uomWholesaleConv`, is a DIFFERENT
// number (ST3367 is conv 30 with no carton quantity at all) and must never be
// read as a carton size.
export const CARTON_QUANTITY_FREIGHT_KEY = 'outer_tu5item_quantity';

/**
 * Coerce a raw attribute value to a displayable carton quantity.
 *
 * Per AC: absent, empty, zero, or "not a positive whole number" all hide the
 * row; a quantity of 1 is displayed normally rather than suppressed.
 *
 * Accepts a number OR a numeric string — the live feed emits both (see the
 * measured shapes above).
 */
export const parseCartonQuantity = (raw: unknown): number | undefined => {
  let value: number;

  if (typeof raw === 'number') {
    value = raw;
  } else if (typeof raw === 'string') {
    const trimmed = raw.trim();
    // Reject '' and anything non-numeric outright — Number('') is 0, which
    // would otherwise sail through as a falsy-but-defined value.
    //
    // The optional `.0+` tail is not cosmetic: the live feed formats two of
    // the five populated products as "60.0" (QM1500) and "800.0" (WB1500).
    // The stricter `/^\d+$/` this replaces rejected both, hiding their carton
    // size with no error anywhere — indistinguishable from a product that has
    // none. A real fraction ('60.5') is still rejected by the integer check
    // below, as are '.5', '60.', hex ('0x10' → 16) and exponent ('1e3' → 1000).
    if (!/^\d+(\.0+)?$/.test(trimmed)) return undefined;
    value = Number(trimmed);
  } else {
    return undefined;
  }

  // `isSafeInteger`, not `isInteger`: above 2^53 a value stops surviving the
  // round trip AC-4's "plain whole number" assumes. '9007199254740993' parses
  // to 9007199254740992 (a different integer) and 1e21 stringifies to the text
  // '1e+21', which is what the PDP span would print. Neither is reachable on
  // today's data — the largest live carton quantity is 800 — so this closes the
  // class rather than fixing an observed bug. Every real shape (30, 4, '60.0',
  // '800.0') is well inside the safe range, and NaN / Infinity are still
  // rejected exactly as before.
  if (!Number.isSafeInteger(value) || value < 1) return undefined;
  return value;
};

interface FreightAttributes {
  [CARTON_QUANTITY_FREIGHT_KEY]?: unknown;
}

/**
 * Pull the carton quantity out of a `freightAttributes` payload.
 *
 * Mirrors `derivePackSize`'s tolerant signature (same attribute, same folder):
 * the attribute reaches the FE as a JSON-encoded STRING, but an already-parsed
 * object is accepted too so a future BFF revision that deep-parses it needs no
 * change here. Anything else — a non-object, an array, unparseable JSON, an
 * empty string, or a payload without the key — yields undefined and the PDP
 * row hides, matching brand / MPN / "Sold as" behaviour.
 */
export const deriveCartonQuantity = (freight: unknown): number | undefined => {
  let parsed: unknown = freight;

  if (typeof freight === 'string') {
    if (!freight.trim()) return undefined;
    try {
      parsed = JSON.parse(freight);
    } catch {
      return undefined;
    }
  }

  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return undefined;

  return parseCartonQuantity((parsed as FreightAttributes)[CARTON_QUANTITY_FREIGHT_KEY]);
};
